import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  LockOpen as LockOpenIcon,
  Menu as MenuIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import { get as getRTDB, ref } from 'firebase/database'
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore'
import { auth, db, rtdb } from './api/firebase'

"use client";

const drawerWidth = 240
const collapsedWidth = 70
const buildMarker = 'siteworker-auth-v20260712-1'
type AuthRole = 'site-worker' | 'admin'

type AuthUser = {
  uid: string
  email: string
  displayName: string
  photoURL?: string | null
}

type AuthFormState = {
  email: string
  password: string
}

const roleTitles: Record<AuthRole, string> = {
  admin: 'Admin',
  'site-worker': 'Site Worker',
}

const APP_ID = 'siteworker-web'

async function resolveAppRole(uid: string): Promise<AuthRole | null> {
  const candidates = [
    ref(rtdb, `appRoles/${APP_ID}/${uid}`),
    ref(rtdb, `appAccess/${APP_ID}/${uid}`),
    ref(rtdb, `appRoles/siteworker-web/${uid}`),
    ref(rtdb, `appAccess/siteworker-web/${uid}`),
    ref(rtdb, `admins/${uid}`),
    ref(rtdb, `authors/${uid}`),
  ]

  console.log('[siteworker] resolveAppRole:start', { uid, appId: APP_ID, candidatePaths: candidates.map((candidate) => candidate.toString()) })

  for (const candidate of candidates) {
    const snapshot = await getRTDB(candidate)
    const path = candidate.toString()
    console.log('[siteworker] resolveAppRole:check', { uid, path, exists: snapshot.exists(), value: snapshot.val() })

    if (!snapshot.exists()) {
      continue
    }

    const value = snapshot.val()
    if (typeof value === 'boolean') {
      if (path.includes('/admins/')) {
        console.log('[siteworker] resolveAppRole:matched', { uid, path, role: 'admin' })
        return 'admin'
      }
      const resolvedRole = value ? 'site-worker' : null
      console.log('[siteworker] resolveAppRole:matched', { uid, path, role: resolvedRole })
      return resolvedRole
    }

    if (typeof value === 'string') {
      const normalized = value.toLowerCase()
      if (normalized === 'admin') {
        console.log('[siteworker] resolveAppRole:matched', { uid, path, role: 'admin' })
        return 'admin'
      }
      if (normalized === 'site-worker' || normalized === 'siteworker' || normalized === 'author') {
        console.log('[siteworker] resolveAppRole:matched', { uid, path, role: 'site-worker' })
        return 'site-worker'
      }
    }

    if (typeof value === 'object' && value !== null) {
      const normalized = String((value as { role?: string; access?: string }).role ?? (value as { role?: string; access?: string }).access ?? '').toLowerCase()
      if (normalized === 'admin') {
        console.log('[siteworker] resolveAppRole:matched', { uid, path, role: 'admin' })
        return 'admin'
      }
      if (normalized === 'site-worker' || normalized === 'siteworker' || normalized === 'author') {
        console.log('[siteworker] resolveAppRole:matched', { uid, path, role: 'site-worker' })
        return 'site-worker'
      }
    }
  }

  console.log('[siteworker] resolveAppRole:unmatched', { uid })
  return null
}

export default function App() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [desktopOpen, setDesktopOpen] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [role, setRole] = useState<AuthRole | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoginForm, setIsLoginForm] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null)
  const [formState, setFormState] = useState<AuthFormState>({
    email: '',
    password: '',
  })

  const isDrawerOpen = isMobile ? mobileOpen : desktopOpen

  useEffect(() => {
    console.log('[siteworker] app mounted', { buildMarker })

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[siteworker] auth state changed', { buildMarker, uid: firebaseUser?.uid ?? null })

      if (!firebaseUser?.email) {
        setUser(null)
        setRole(null)
        return
      }

      const profileRef = doc(db, 'users', firebaseUser.uid)
      const existingProfile = await getDoc(profileRef)
      console.log('[siteworker] profile lookup', { uid: firebaseUser.uid, exists: existingProfile.exists() })

      if (!existingProfile.exists()) {
        console.log('[siteworker] creating profile document', { uid: firebaseUser.uid })
        await setDoc(profileRef, {
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          userId: firebaseUser.email.split('@')[0],
          photoURL: firebaseUser.photoURL || null,
          createdAt: serverTimestamp(),
          roles: [],
          appRoles: {},
        })
      }

      const resolvedRole = await resolveAppRole(firebaseUser.uid)
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName ?? firebaseUser.email.split('@')[0],
        photoURL: firebaseUser.photoURL,
      })
      setRole(resolvedRole)
    })

    return () => unsubscribe()
  }, [])

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev)
    } else {
      setDesktopOpen((prev) => !prev)
    }
  }

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    setError(null)

    const email = formState.email.trim().toLowerCase()
    const password = formState.password

    try {
      console.log('[siteworker] submit auth form', {
        buildMarker,
        mode: isLoginForm ? 'sign-in' : 'register',
        email,
      })

      if (isLoginForm) {
        const result = await signInWithEmailAndPassword(auth, email, password)
        const resolvedRole = await resolveAppRole(result.user.uid)

        console.log('[siteworker] sign-in form', {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          resolvedRole,
        } )

        setUser({
          uid: result.user.uid,
          email: result.user.email ?? email,
          displayName: result.user.displayName ?? result.user.email?.split('@')[0] ?? email,
          photoURL: result.user.photoURL,
        })
        setRole(resolvedRole)
      } else {
        const result = await createUserWithEmailAndPassword(auth, email, password)
        const userProfile = {
          email: result.user.email,
          displayName: result.user.displayName || `User ${result.user.uid.substring(0, 5)}`,
          userId: email.split('@')[0],
          photoURL: result.user.photoURL || null,
          createdAt: serverTimestamp(),
          roles: [],
          appRoles: {},
        }

        console.log('[siteworker] register form', {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
        } )



        await setDoc(doc(db, 'users', result.user.uid), userProfile)
        const resolvedRole = await resolveAppRole(result.user.uid)
        setUser({
          uid: result.user.uid,
          email: result.user.email ?? email,
          displayName: result.user.displayName ?? result.user.email?.split('@')[0] ?? email,
          photoURL: result.user.photoURL,
        })
        setRole(resolvedRole)
      }
    } catch (signinError) {
      console.error(signinError)
      setError('Unable to complete that request. Please check the email and password.')
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth)
    } finally {
      setUser(null)
      setRole(null)
      setProfileMenuAnchor(null)
    }
  }

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev)
  const openProfileMenu = (event: MouseEvent<HTMLElement>) => setProfileMenuAnchor(event.currentTarget)
  const closeProfileMenu = () => setProfileMenuAnchor(null)

  const drawerContent = (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      <List>
        {[
          { text: 'Overview', icon: <DashboardIcon /> },
          { text: 'Users', icon: <PeopleIcon /> },
          { text: 'Metrics', icon: <BarChartIcon /> },
          { text: 'Logs', icon: <HistoryIcon /> },
          { text: 'Settings', icon: <SettingsIcon /> },
        ].map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: isDrawerOpen ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {isDrawerOpen && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  )

  if (!user || !role) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockOpenIcon color="primary" />
                <Typography variant="h5">Secure dashboard access</Typography>
              </Box>
              <Typography color="text.secondary">
                Use Firebase Authentication to sign in or register a new account for the role you need.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <Box component="form" onSubmit={handleSignIn} sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={formState.email}
                  onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                  required
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formState.password}
                  onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <IconButton edge="end" onClick={togglePasswordVisibility}>
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    },
                  }}
                  required
                />
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? (isLoginForm ? 'Signing in…' : 'Creating account…') : isLoginForm ? 'Sign in' : 'Register'}
                </Button>
              </Box>
              <Button color="secondary" onClick={() => setIsLoginForm((prev) => !prev)}>
                {isLoginForm ? "Need an account? Register" : 'Already have an account? Sign in'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                Access is resolved from your shared Firebase profile and the RTDB role nodes for this app.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Debug build: {buildMarker}
              </Typography>
            </Stack>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflowX: 'hidden' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Siteworker Dashboard
          </Typography>
          {user && (
            <>
              <IconButton color="inherit" onClick={openProfileMenu} sx={{ ml: 1 }}>
                <Avatar src={user.photoURL ?? undefined} sx={{ width: 28, height: 28 }}>
                  {user.displayName?.[0]?.toUpperCase() ?? 'U'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={closeProfileMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user.email}</Typography>
                </MenuItem>
                <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? drawerWidth : isDrawerOpen ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isMobile ? drawerWidth : isDrawerOpen ? drawerWidth : collapsedWidth,
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: '100%',
          overflowX: 'hidden',
        }}
      >
        <Toolbar />
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                System Overview
              </Typography>
              <Typography color="text.secondary">
                Signed in as {user.displayName} · {roleTitles[role]}
              </Typography>
            </Box>
            <Paper variant="outlined" sx={{ px: 2, py: 1, bgcolor: role === 'admin' ? 'rgba(46,125,50,0.12)' : 'rgba(25,118,210,0.12)' }}>
              <Typography variant="body2" fontWeight={600}>
                {role === 'admin' ? 'Administrator access' : 'Site worker access'}
              </Typography>
            </Paper>
          </Stack>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} lg={4}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 140 }}>
                <Typography color="primary" variant="h6">Active Users</Typography>
                <Typography variant="h3">24</Typography>
                <Typography variant="caption">Live from Firebase</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 140 }}>
                <Typography color="secondary" variant="h6">System Logs</Typography>
                <Typography variant="h3">1.2k</Typography>
                <Typography variant="caption">Last 24 hours</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 140 }}>
                <Typography sx={{ color: 'success.main' }} variant="h6">Database State</Typography>
                <Typography variant="h3">Healthy</Typography>
                <Typography variant="caption">All clusters operational</Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
                <Typography variant="h6">Operational Trends</Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    mt: 2,
                    minHeight: 220,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Real-time chart will go here...
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}
